import { useEffect } from 'react';
import { notify } from '../lib/notify';
import { useNotificationActions } from '../stores/notificationStore';
import { useAuthStore, selectIsSuperAdmin } from '../stores/authStore';
import { NotificationCategory } from '../types/notification';

const variantToCategory: Record<string, NotificationCategory> = {
  success: 'success',
  error: 'error',
  warning: 'warning',
  info: 'info',
};

/**
 * Subscribes to the toast event bus and mirrors every explicit notify.* call
 * into the notification store.
 *
 * Global window `error` / `unhandledrejection` listeners are intentionally
 * NOT captured here — they created a feedback loop where React render errors
 * triggered notifications that caused more renders. Errors must be added to
 * the notification store explicitly via notify.* calls in application code.
 *
 * Mount once inside the authenticated layout tree.
 */
export function useNotificationBridge() {
  const { addNotification } = useNotificationActions();
  const userId = useAuthStore((state) => state.user?.id ?? null);

  useEffect(() => {
    if (!userId) return;

    const unsubscribeToast = notify.subscribe((msg) => {
      // System-level errors (API/backend/infrastructure) are only stored for super_admins
      const isSystemError = msg.isSystemError ?? false;
      const isSuperAdmin = selectIsSuperAdmin(useAuthStore.getState().user);
      if (isSystemError && !isSuperAdmin) return;

      // Defer store update to avoid "Maximum update depth exceeded": if this
      // callback runs synchronously during another effect (e.g. useTourGuard
      // calling notify.info()), updating the store here would trigger a
      // re-render of AppHeader mid-commit and React throws.
      queueMicrotask(() => {
        // Guard against callers accidentally passing objects instead of strings
        const safeStr = (v: unknown) =>
          typeof v === 'string' ? v : v != null ? JSON.stringify(v) : undefined;
        addNotification(userId, {
          title: safeStr(msg.title),
          description: safeStr(msg.description),
          link: msg.link,
          category: variantToCategory[msg.variant ?? 'info'] ?? 'info',
          isSystemError,
          systemErrorDetails: msg.systemErrorDetails,
        });
      });
    });

    return () => {
      unsubscribeToast();
    };
  }, [userId, addNotification]);
}
